import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.loading(true, false);
    //this.animateAddToCart();

    // if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };


  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * @param {boolean} show
   * @param {boolean} success
   */
  loading(show, success){
    const { addToCartButton } = this.refs;
    
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if(show){
      if (!addToCartButton.classList.contains('atc-loading')) {
        addToCartButton.classList.add('atc-loading');
      }
    }else{
      addToCartButton.classList.remove('atc-loading');
      // Added:
      if(success){
        this.animateAddToCart();
      }
    }
  }
  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    // Check if product has BOGOF tag
    const productTags = this.dataset.productTags || '';
    const isBogof = productTags.split(',').includes('bogof');
    
    // Get current quantity from form input or default
    const quantityInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="quantity"]'));
    const currentQuantity = quantityInput 
      ? Number(quantityInput.value) || 1
      : Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    
    // For BOGOF products with quantity 1, add as two separate line items
    // This ensures the discount rule matches correctly (Buy 1, Get 1 Free)
    if (isBogof && currentQuantity === 1) {
      this.#addBogofItemsAsSeparate(form, formData);
      return;
    }
    
    // For BOGOF with quantity > 1, or non-BOGOF products, use normal flow
    if (isBogof && currentQuantity > 1) {
      formData.set('quantity', String(currentQuantity));
      formData.append('properties[_bogof]', 'true');
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length > 0) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then(response => response.json())
      .then((response) => {
        // Instead of updating DOM directly here, defer to rAF
        requestAnimationFrame(() => {
          if (response.status) {
            // Show error
            if (addToCartTextError) {
              addToCartTextError.classList.remove('hidden');
              addToCartTextError.textContent = response.message; // only update once
            }

            if ('requestIdleCallback' in window) {
              requestIdleCallback(() => this.#setLiveRegionText(response.message));
            } else {
              setTimeout(() => this.#setLiveRegionText(response.message), 0);
            }

            setTimeout(() => {
              if (addToCartTextError) addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, 10000);

            this.dispatchEvent(
              new CartAddEvent({}, this.id, {
                didError: true,
                source: 'product-form-component',
                itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
                productId: this.dataset.productId,
              })
            );
          } else {
            // Success case DOM updates
            requestAnimationFrame(() => {
              if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
                // Remove loading
                this.refs.addToCartButtonContainer.loading(false, true)
                
                const addedTextElement = this.refs.addToCartButtonContainer.refs.addToCartButton.querySelector('.add-to-cart-text--added');
                const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
                

                if ('requestIdleCallback' in window) {
                  requestIdleCallback(() => this.#setLiveRegionText(addedText));
                } else {
                  setTimeout(() => this.#setLiveRegionText(addedText), 0);
                }

                setTimeout(() => this.#clearLiveRegionText(), 5000);
              }

              const variantId = formData.get('id');
              this.dispatchEvent(
                new CartAddEvent({}, variantId ? variantId.toString() : '', {
                  source: 'product-form-component',
                  itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
                  productId: this.dataset.productId,
                  sections: response.sections,
                })
              );
            });
          }
        });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Adds BOGOF items as two separate line items (two API calls)
   * This ensures the discount rule "Buy 1, Get 1 Free" matches correctly
   * @param {HTMLFormElement} form - The product form
   * @param {FormData} formData - The form data
   */
  #addBogofItemsAsSeparate(form, formData) {
    const { addToCartTextError } = this.refs;
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    // Create a unique identifier for this BOGOF product pair
    // This helps Shopify distinguish between different BOGOF products in the cart
    const bogofPairId = `bogof-${this.dataset.productId}-${Date.now()}`;
    
    // Create form data for first item (paid item)
    const firstItemData = new FormData(form);
    firstItemData.set('quantity', '1');
    firstItemData.append('properties[_bogof]', 'paid');
    firstItemData.append('properties[_bogof_pair]', bogofPairId);
    if (cartItemComponentsSectionIds.length > 0) {
      firstItemData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    // Create form data for second item (free item)
    const secondItemData = new FormData(form);
    secondItemData.set('quantity', '1');
    secondItemData.append('properties[_bogof]', 'free');
    secondItemData.append('properties[_bogof_pair]', bogofPairId);
    if (cartItemComponentsSectionIds.length > 0) {
      secondItemData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    const fetchCfg1 = fetchConfig('javascript', { body: firstItemData });
    
    // Add first item
    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg1,
      headers: {
        ...fetchCfg1.headers,
        Accept: 'text/html',
      },
    })
      .then(response => response.json())
      .then((firstResponse) => {
        if (firstResponse.status) {
          // Error on first item
          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            addToCartTextError.textContent = firstResponse.message;
          }
          this.#setLiveRegionText(firstResponse.message);
          setTimeout(() => {
            if (addToCartTextError) addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, 10000);
          return;
        }

        // First item added successfully, now add second item immediately
        // Using a simpler approach - add second item right away without delays
        // The unique pair ID should help Shopify distinguish between different BOGOF products
        
        // Update sections from first response to ensure cart state is current
        if (firstResponse.sections && Object.keys(firstResponse.sections).length > 0) {
          // Get updated section IDs from the cart
          const updatedCartItemsComponents = document.querySelectorAll('cart-items-component');
          /** @type {string[]} */
          const updatedSectionIds = [];
          updatedCartItemsComponents.forEach((item) => {
            if (item instanceof HTMLElement && item.dataset.sectionId) {
              updatedSectionIds.push(item.dataset.sectionId);
            }
          });
          if (updatedSectionIds.length > 0) {
            secondItemData.set('sections', updatedSectionIds.join(','));
          }
        }
        
        const fetchCfg2 = fetchConfig('javascript', { body: secondItemData });
        
        return fetch(Theme.routes.cart_add_url, {
          ...fetchCfg2,
          headers: {
            ...fetchCfg2.headers,
            Accept: 'text/html',
          },
        })
          .then(response => response.json())
          .then((secondResponse) => {
            requestAnimationFrame(() => {
              if (secondResponse.status) {
              // Error on second item (but first was added)
              if (addToCartTextError) {
                addToCartTextError.classList.remove('hidden');
                addToCartTextError.textContent = secondResponse.message || 'First item added, but failed to add second item';
              }
              this.#setLiveRegionText(secondResponse.message || 'First item added, but failed to add second item');
              setTimeout(() => {
                if (addToCartTextError) addToCartTextError.classList.add('hidden');
                this.#clearLiveRegionText();
              }, 10000);
              
              // Still dispatch cart:add event since first item was added
              const variantId1 = firstItemData.get('id');
              this.dispatchEvent(
                new CartAddEvent({}, variantId1 ? variantId1.toString() : '', {
                  source: 'product-form-component',
                  itemCount: 1,
                  productId: this.dataset.productId,
                  sections: firstResponse.sections,
                })
              );
              return;
            }

            // Both items added successfully
            // Force cart refresh to trigger discount re-evaluation
            setTimeout(() => {
              fetch(Theme.routes.cart_url + '.js')
                .then(response => response.json())
                .then((cartData) => {
                  // Check ALL BOGOF items in cart to see if discounts are missing
                  const bogofItems = cartData.items.filter((/** @type {any} */ item) => {
                    return item.properties && 
                      (item.properties._bogof === 'free' || item.properties._bogof === 'paid' || item.properties._bogof === 'true');
                  });
                  
                  // Group BOGOF items by product to check pairs
                  /** @type {Record<string, any[]>} */
                  const productGroups = {};
                  bogofItems.forEach((/** @type {any} */ item) => {
                    const productId = String(item.product_id);
                    if (!productGroups[productId]) {
                      productGroups[productId] = [];
                    }
                    productGroups[productId].push(item);
                  });
                  
                  // Check if any product pair is missing discounts
                  let needsRefresh = false;
                  Object.values(productGroups).forEach((/** @type {any[]} */ items) => {
                    // Each product should have 2 items, and one should be free
                    if (items.length === 2) {
                      const hasFreeItem = items.some((/** @type {any} */ item) => 
                        item.final_price === 0 || (item.discount_allocations && item.discount_allocations.length > 0)
                      );
                      if (!hasFreeItem) {
                        needsRefresh = true;
                      }
                    }
                  });
                  
                  if (needsRefresh && cartData.items.length > 0) {
                    // Strategy 1: Update cart note to force full refresh
                    fetch(Theme.routes.cart_update_url, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                        note: cartData.note || ' ',
                      }),
                    })
                      .then(() => {
                        // Strategy 2: Also update all item quantities
                        const updateData = new FormData();
                        cartData.items.forEach((/** @type {any} */ item) => {
                          if (item.key) {
                            updateData.append(`updates[${item.key}]`, String(item.quantity));
                          }
                        });
                        
                        return fetch(Theme.routes.cart_update_url, {
                          method: 'POST',
                          body: updateData,
                        })
                          .then(() => {
                            // Strategy 3: Dispatch cart:update event to refresh UI
                            setTimeout(() => {
                              document.dispatchEvent(
                                new CartUpdateEvent({}, this.dataset.sectionId || '', {
                                  source: 'product-form-component',
                                })
                              );
                            }, 200);
                          })
                          .catch(() => {});
                      })
                      .catch(() => {});
                  }
                })
                .catch(() => {});
            }, 1000); // 1 second delay before checking and refreshing
            
            requestAnimationFrame(() => {
              if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
                this.refs.addToCartButtonContainer.loading(false, true);
                
                const addedTextElement = this.refs.addToCartButtonContainer.refs.addToCartButton.querySelector('.add-to-cart-text--added');
                const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

                if ('requestIdleCallback' in window) {
                  requestIdleCallback(() => this.#setLiveRegionText(addedText));
                } else {
                  setTimeout(() => this.#setLiveRegionText(addedText), 0);
                }

                setTimeout(() => this.#clearLiveRegionText(), 5000);
              }

              // Dispatch cart:add event with combined sections
              const variantId2 = firstItemData.get('id');
              const combinedSections = { ...firstResponse.sections, ...secondResponse.sections };
              this.dispatchEvent(
                new CartAddEvent({}, variantId2 ? variantId2.toString() : '', {
                  source: 'product-form-component',
                  itemCount: 2,
                  productId: this.dataset.productId,
                  sections: combinedSections,
                })
              );
            });
          });
        })
        .catch((error) => {
          console.error('Error adding BOGOF items:', error);
          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            addToCartTextError.textContent = 'Error adding items to cart';
          }
          this.#setLiveRegionText('Error adding items to cart');
          setTimeout(() => {
            if (addToCartTextError) addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, 10000);
        });
      })
      .catch((error) => {
        console.error('Error adding BOGOF items:', error);
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');
          addToCartTextError.textContent = 'Error adding items to cart';
        }
        this.#setLiveRegionText('Error adding items to cart');
        setTimeout(() => {
          if (addToCartTextError) addToCartTextError.classList.add('hidden');
          this.#clearLiveRegionText();
        }, 10000);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    // Cache layout reads upfront to avoid thrashing later
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    const offsetX = this.offsetWidth / 2;
    const offsetY = this.offsetHeight / 2;

    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offsetX,
      y: sourceRect.top + sourceRect.height / 2 - offsetY,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offsetX,
      y: destinationRect.top + destinationRect.height / 2 - offsetY,
    };

    // Precompute control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 };
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 };

    const duration = 600;
    /** @type {number | null} */
    let startTime = null;

    this.style.opacity = '1';

    // Position at the starting point immediately
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);
    this.style.setProperty('--scale', '1');

    /**
     * @param {number} currentTime
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - (startTime || 0);
      const progress = Math.min(elapsed / duration, 1);

      // Calculate position along curve
      const { x, y } = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      this.style.setProperty('--x', `${x}px`);
      this.style.setProperty('--y', `${y}px`);

      // Scale down smoothly toward the end
      this.style.setProperty('--scale', `${1 - progress * 0.5}`);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Fade out, then remove
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
